import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import AthleteHeadshot from './AthleteHeadshot';
import AthleteProfile from './AthleteProfile';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Photo roster for the caller's active team (admins see the whole club,
 * grouped by team). Coaches/admins can open a profile from a card.
 */
export default function TeamRoster() {
  const { token, user, isCoach, isAdmin } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewingAthlete, setViewingAthlete] = useState(null);
  const canOpenProfile = isCoach() || isAdmin();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const team = (user?.role === 'admin' || user?.role === 'superadmin') ? undefined : user?.team;
      const query = team ? `?team=${encodeURIComponent(team)}` : '';
      const response = await fetch(`${API_URL}/api/athletes${query}`, {
        headers: getAuthHeaders(token)
      });
      if (!response.ok) {
        throw new Error('Failed to load roster');
      }
      const data = await response.json();
      setAthletes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[TeamRoster]', err);
      setError(err.message || 'Failed to load roster');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token, user?.team, user?.role]);

  const groups = useMemo(() => {
    const byTeam = new Map();
    athletes.forEach((athlete) => {
      const key = athlete.team || 'Unassigned';
      if (!byTeam.has(key)) byTeam.set(key, []);
      byTeam.get(key).push(athlete);
    });
    return [...byTeam.entries()].sort(([a], [b]) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
  }, [athletes]);

  if (viewingAthlete) {
    return (
      <AthleteProfile
        athlete={viewingAthlete}
        onBack={() => {
          setViewingAthlete(null);
          load();
        }}
        onSave={null}
        readOnly
      />
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Team Roster</h2>
        <p className="text-sm text-gray-500">
          {user?.team ? `${user.team} headshots and roster.` : 'Club roster with headshots.'}
        </p>
      </div>

      {loading ? (
        <p className="text-gray-500 text-sm">Loading roster...</p>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
      ) : athletes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400">
          No athletes on this roster yet.
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map(([teamName, members]) => (
            <section key={teamName}>
              {groups.length > 1 && (
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                  {teamName} ({members.length})
                </h3>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {members.map((athlete) => {
                  const clickable = canOpenProfile;
                  const Wrapper = clickable ? 'button' : 'div';
                  return (
                    <Wrapper
                      key={athlete.id}
                      type={clickable ? 'button' : undefined}
                      onClick={clickable ? () => setViewingAthlete(athlete) : undefined}
                      className={`bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center text-center ${
                        clickable ? 'hover:border-theme-primary hover:shadow-sm transition cursor-pointer' : ''
                      }`}
                    >
                      <AthleteHeadshot
                        name={athlete.name}
                        headshotUrl={athlete.headshot_url}
                        size="lg"
                      />
                      <p className={`mt-3 font-semibold text-gray-800 leading-tight ${athlete.is_injured ? 'text-red-700' : ''}`}>
                        {athlete.name}
                      </p>
                      {athlete.racingclass && (
                        <p className="text-xs text-gray-500 mt-0.5">{athlete.racingclass}</p>
                      )}
                      {athlete.is_injured && (
                        <span className="mt-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">
                          Injured
                        </span>
                      )}
                    </Wrapper>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
